import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getReleases } from '../../services/homepage-service'

import {
  archiveEvents,
  getEvents,
  getItemSetupEvents,
  submit,
  searchEvents,
  fetchEventContacts,
  unarchiveEvents,
  getEventAttributes,
  updateEventAttributes,
  getDistBps,
} from '../../services/events-service'

import {
  getUserEvents,
  getAssociatedPlans,
  searchEvents as searchEventsInternal,
  getAuthUsers,
  getEventUserViews,
  submitBid,
} from '../../services/internal/events-service'

import { getVendorsByEvent } from '../../services/internal/vendor-grid-service'
import {
  getProductCount,
  getProductCountForBp,
} from '../../services/products-service'
import {
  getPMUFieldsForVendor,
  getPMUFields,
} from '../../components/ValidatePMU/PMUAttributesBuilder'
import { RootState } from 'store/store'

export const fetchEventsAction = createAsyncThunk<
  any,
  { page; perPage; sortByBidTs },
  { state: RootState }
>(
  'events/fetchEvents',
  async (
    data: { page; perPage; sortByBidTs },
    { getState }: { getState: () => RootState },
  ) => {
    const { page, perPage, sortByBidTs } = data
    const bpId = getState().auth.partnerId
    return await getEvents(page, perPage, bpId, sortByBidTs, false)
  },
)

export const fetchInternalUserEventsAction = createAsyncThunk(
  'events/fetchInternalUserEvents',
  async (data: { isUserEvent; page; perPage }) => {
    return await getUserEvents(data)
  },
)

export const fetchReleasesAction = createAsyncThunk(
  'events/fetchReleases',
  async () => {
    return await getReleases()
  },
)

export const fetchItemSetupEventsAction = createAsyncThunk<
  any,
  { page; perPage },
  { state: RootState }
>(
  'events/fetchItemSetupEvents',
  async (
    data: { page; perPage },
    { getState }: { getState: () => RootState },
  ) => {
    const { page, perPage } = data || {}
    const bpId = getState().auth.partnerId
    return await getItemSetupEvents(page, perPage, bpId)
  },
)

export const archiveEventsAction = createAsyncThunk(
  'events/archiveEvents',
  async (data: { eventObjectid; bidId }) => {
    const { eventObjectid, bidId } = data
    return await archiveEvents(eventObjectid, bidId)
  },
)

export const fetchArchivedEventsAction = createAsyncThunk<
  any,
  { page; perPage; sortByBidTs },
  { state: RootState }
>(
  'events/fetchArchivedEvents',
  async (
    data: { page; perPage; sortByBidTs },
    { getState }: { getState: () => RootState },
  ) => {
    const { page = 1, perPage = 1, sortByBidTs = false } = data
    const bpId = getState().auth.partnerId
    return await getEvents(page, perPage, bpId, sortByBidTs, true)
  },
)

export const unarchiveEventsAction = createAsyncThunk(
  'events/unarchiveEvents',
  async (data: { eventObjectid; bidId; eventName }) => {
    const { eventObjectid, bidId, eventName } = data
    return await unarchiveEvents(eventObjectid, bidId, eventName)
  },
)

export const submitEventsAction = createAsyncThunk(
  'events/submitEvents',
  async (bid: any) => {
    bid.status_key = 'submitted_to_target'
    return await submit(
      bid,
      'Bid submitted successfully. Email was generated and sent.',
      'Failed to submit the bid',
      true,
    )
  },
)

export const updateBidAction = createAsyncThunk(
  'events/updateBid',
  async (data: { bid; successMessage; errorMessage }) => {
    const {
      bid,
      successMessage = 'Updated successfully.',
      errorMessage = 'Failed to update.',
    } = data
    return await submit(bid, successMessage, errorMessage)
  },
)

export const searchEventsAction = createAsyncThunk<
  any,
  { page; perPage; eventName; eventId },
  { state: RootState }
>(
  'events/searchEvents',
  async (
    data: { page; perPage; eventName; eventId },
    { getState }: { getState: () => RootState },
  ) => {
    const { page, perPage, eventName, eventId } = data
    const bpId = getState().auth.partnerId
    return await searchEvents(page, perPage, bpId, eventName, eventId)
  },
)

export const fetchEventContactsAction = createAsyncThunk(
  'events/fetchEventContacts',
  async (eventId: string) => {
    return await fetchEventContacts(eventId)
  },
)

export const fetchEventAttributesAction = createAsyncThunk(
  'events/fetchEventAttributes',
  async (data: { eventId; bidId }) => {
    const { eventId, bidId } = data
    return await getEventAttributes(eventId, bidId)
  },
)

export const fetchEventAttributesForPMUAction = createAsyncThunk(
  'events/fetchEventAttributesForPMU',
  async (
    data: {
      eventId
      bidId
      impVendorIds
      domVendorIds
      dvsVendorIds
    },
    { getState },
  ) => {
    const state = getState() as any
    const { eventId, bidId, impVendorIds, domVendorIds, dvsVendorIds } = data
    const res = await getEventAttributes(eventId, bidId)

    const eventAttributes = res.data
    const eventAttributePaths = eventAttributes.map(
      ({ attribute }) => 'section_data.' + attribute,
    )

    if (eventAttributePaths.length > 0) {
      const pmuAttributes = bidId
        ? getPMUFieldsForVendor({
            hasImportVendors: impVendorIds?.length > 0,
            hasDomesticVendors: domVendorIds?.length > 0,
            hasDvsVendors: dvsVendorIds?.length > 0,
            state: state,
          })
        : getPMUFields(state)

      const missingPMUAttributes = pmuAttributes
        .filter(attribute => !eventAttributePaths.includes(attribute))
        .map(missingAttribute => {
          const [, section, attribute] = missingAttribute.split('.')
          return {
            attribute: `${section}.${attribute}`,
            event_level: !bidId,
            status: 'E',
          }
        })

      if (missingPMUAttributes.length > 0) {
        const updated = [...eventAttributes, ...missingPMUAttributes]
        await updateEventAttributesAction({
          eventId,
          bidId,
          requestBody: updated,
        })
        return {
          ...res,
          data: updated,
        }
      }
    }
    return res
  },
)

export const updateEventAttributesAction = createAsyncThunk(
  'events/updateEventAttributes',
  async (data: { eventId; bidId; requestBody }) => {
    const { eventId, bidId, requestBody } = data
    return await updateEventAttributes(eventId, bidId, requestBody)
  },
)

export const fetchAssociatedPlansAction = createAsyncThunk(
  'events/fetchAssociatedPlans',
  async (data: { eventId }) => {
    return await getAssociatedPlans(data)
  },
)

export const fetchVendorCountAction = createAsyncThunk(
  'events/fetchVendorCount',
  async (data: { eventId }) => {
    return await getVendorsByEvent({ ...data, page: 1, perPage: 1 })
  },
)

export const fetchProductCountAction = createAsyncThunk(
  'events/fetchProductCount',
  async (data: { eventId: string; bpId; isBusinessConfirmed }) => {
    return await getProductCount(data)
  },
)

export const fetchProductCountActionForBp = createAsyncThunk(
  'events/fetchProductCountForBp',
  async (data: { eventId; bpId; isApparelEvent }) => {
    return await getProductCountForBp(data)
  },
)

export const searchEventsInternalAction = createAsyncThunk(
  'events/searchEventsInternal',
  async (data: {
    page
    perPage
    eventName
    eventId
    userName
    devCycleIds
    devCycleYearIds
    departmentId
    clazzId
    brand
    levelGroup
    bpName
  }) => {
    return await searchEventsInternal(data)
  },
)

export const getAuthUsersAction = createAsyncThunk(
  'events/getAuthUsers',
  async (eventId: any) => {
    return await getAuthUsers(eventId)
  },
)

export const getEventUserViewsAction = createAsyncThunk(
  'events/getEventUserViews',
  async (eventId: string) => {
    return await getEventUserViews(eventId)
  },
)

export const updateBidForInternalAction = createAsyncThunk(
  'events/updateBidForInternal',
  async (data: { eventId; bidId; request; successMessage; errorMessage }) => {
    const {
      eventId,
      bidId,
      request,
      successMessage = 'Updated successfully.',
      errorMessage = 'Failed to update.',
    } = data
    return await submitBid(
      eventId,
      bidId,
      request,
      successMessage,
      errorMessage,
    )
  },
)

export const fetchDistBps = createAsyncThunk(
  'events/fetchDistBps',
  async (bpId: any) => {
    return await getDistBps(bpId)
  },
)

type SearchCriteria = {
  eventName
  eventId
  userName
  page
}

const initialState = {
  isFetchingArchivedEvents: false,
  archivedEventsList: [],
  archivedEventsTotalCount: 0,
  isFetchingEvents: false,
  eventList: [] as any[],
  eventsPageCount: 0,
  eventsTotalCount: 0,
  isFetchingReleases: false,
  releases: [],
  isFetchingItemSetupEvents: false,
  itemSetupEventsList: [],
  itemsWaitingItemSetupCount: 0,
  isFetchingContactList: false,
  contactList: [],
  isFetchingEventAttributes: false,
  eventAttributes: [],
  isEventAttributesUpdating: false,
  isFetchingAssociatedPlans: false,
  eventPlansMap: {},
  isFetchingVendorCount: false,
  eventVendorCountMap: {},
  isFetchingProductCount: false,
  eventProductCountMap: {},
  authUserList: [],
  isFetchingAuthUsers: false,
  isFetchingEventUserViews: false,
  authUserListWithView: {},
  viewList: [],
  eventProductCountForBpMap: {},
  isFetchingProductCountForBp: false,
  event: {},
  isFetchingDistBps: false,
  distBpsList: [],
  searchCriteria: null as SearchCriteria | null,
  hasSearched: false,
}

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    setSearchCriteriaAction: (state, action) => {
      const { eventName, eventId, userName, page } = action.payload

      state.searchCriteria = {
        eventName,
        eventId,
        userName,
        page,
      }
      state.hasSearched = true
    },
    clearSearchCriteriaAction: state => {
      state.searchCriteria = null
      state.hasSearched = false
    },
    resetEventsAction: state => {
      state.archivedEventsList = []
      state.eventList = []
      state.itemSetupEventsList = []
      state.archivedEventsTotalCount = 0
      state.itemsWaitingItemSetupCount = 0
      state.eventsPageCount = 0
      state.eventsTotalCount = 0
    },
    resetEventUserViewsAction: state => {
      state.isFetchingEventUserViews = false
      state.authUserListWithView = {}
      state.viewList = []
      state.event = {}
    },
  },
  extraReducers: builder => {
    builder

      // fetchEventsAction
      .addCase(fetchEventsAction.pending, state => {
        state.isFetchingEvents = true
      })
      .addCase(fetchEventsAction.fulfilled, (state, action) => {
        const { events, per_page, total_count } = action.payload.data
        state.isFetchingEvents = false
        state.eventList = events
        state.eventsPageCount = per_page
        state.eventsTotalCount = total_count
      })
      .addCase(fetchEventsAction.rejected, state => {
        state.isFetchingEvents = false
        state.eventList = []
        state.eventsPageCount = 0
        state.eventsTotalCount = 0
      })

      // fetchInternalUserEventsAction
      .addCase(fetchInternalUserEventsAction.pending, state => {
        state.isFetchingEvents = true
      })
      .addCase(fetchInternalUserEventsAction.fulfilled, (state, action) => {
        const { events, per_page, total_count } = action.payload.data
        state.isFetchingEvents = false
        state.eventList = events
        state.eventsPageCount = per_page
        state.eventsTotalCount = total_count
      })
      .addCase(fetchInternalUserEventsAction.rejected, state => {
        state.isFetchingEvents = false
        state.eventList = []
        state.eventsPageCount = 0
        state.eventsTotalCount = 0
      })

      // fetchArchivedEventsAction
      .addCase(fetchArchivedEventsAction.pending, state => {
        state.isFetchingArchivedEvents = true
      })
      .addCase(fetchArchivedEventsAction.fulfilled, (state, action) => {
        const { events, total_count } = action.payload.data

        state.isFetchingArchivedEvents = false
        state.archivedEventsList = events
        state.archivedEventsTotalCount = total_count
      })
      .addCase(fetchArchivedEventsAction.rejected, state => {
        state.isFetchingArchivedEvents = false
        state.archivedEventsList = []
        state.archivedEventsTotalCount = 0
      })

      // fetchReleasesAction
      .addCase(fetchReleasesAction.pending, state => {
        state.isFetchingReleases = true
      })
      .addCase(fetchReleasesAction.fulfilled, (state, action) => {
        const { payload } = action

        state.isFetchingReleases = false
        state.releases = payload.data.releases
      })
      .addCase(fetchReleasesAction.rejected, state => {
        state.isFetchingReleases = false
        state.releases = []
      })

      // fetchItemSetupEventsAction
      .addCase(fetchItemSetupEventsAction.pending, state => {
        state.isFetchingItemSetupEvents = true
      })
      .addCase(fetchItemSetupEventsAction.fulfilled, (state, action) => {
        const { payload } = action

        state.isFetchingItemSetupEvents = false
        state.itemsWaitingItemSetupCount = payload.data.total_count
        state.itemSetupEventsList = payload.data.events
      })
      .addCase(fetchItemSetupEventsAction.rejected, state => {
        state.isFetchingItemSetupEvents = false
        state.itemsWaitingItemSetupCount = 0
        state.itemSetupEventsList = []
      })

      // searchEventsAction
      .addCase(searchEventsAction.pending, state => {
        state.isFetchingEvents = true
      })
      .addCase(searchEventsAction.fulfilled, (state, action) => {
        const { events, per_page, total_count } = action.payload.data

        state.isFetchingEvents = false
        state.eventList = events
        state.eventsPageCount = per_page
        state.eventsTotalCount = total_count
      })
      .addCase(searchEventsAction.rejected, state => {
        state.isFetchingEvents = false
        state.eventList = []
        state.eventsPageCount = 0
        state.eventsTotalCount = 0
      })

      // fetchEventContactsAction
      .addCase(fetchEventContactsAction.pending, state => {
        state.isFetchingContactList = true
      })
      .addCase(fetchEventContactsAction.fulfilled, (state, action) => {
        const { payload } = action

        state.isFetchingContactList = false
        state.contactList = payload.data
      })
      .addCase(fetchEventContactsAction.rejected, state => {
        state.isFetchingContactList = false
        state.contactList = []
      })

      // fetchEventAttributesAction
      .addCase(fetchEventAttributesAction.pending, state => {
        state.isFetchingEventAttributes = true
      })
      .addCase(fetchEventAttributesAction.fulfilled, (state, action) => {
        const { payload } = action

        state.isFetchingEventAttributes = false
        state.eventAttributes = payload.data
      })
      .addCase(fetchEventAttributesAction.rejected, state => {
        state.isFetchingEventAttributes = false
        state.eventAttributes = []
      })

      // fetchEventAttributesForPMUAction
      .addCase(fetchEventAttributesForPMUAction.pending, state => {
        state.isFetchingEventAttributes = true
      })
      .addCase(fetchEventAttributesForPMUAction.fulfilled, (state, action) => {
        const { payload } = action

        state.isFetchingEventAttributes = false
        state.eventAttributes = payload.data
      })
      .addCase(fetchEventAttributesForPMUAction.rejected, state => {
        state.isFetchingEventAttributes = false
        state.eventAttributes = []
      })

      // updateEventAttributesAction
      .addCase(updateEventAttributesAction.pending, state => {
        state.isEventAttributesUpdating = true
      })
      .addCase(updateEventAttributesAction.fulfilled, state => {
        state.isEventAttributesUpdating = false
      })
      .addCase(updateEventAttributesAction.rejected, state => {
        state.isEventAttributesUpdating = false
      })

      // updateBidAction
      .addCase(updateBidAction.fulfilled, (state, action) => {
        const updatedBid = action.payload.data
        const eventList = state.eventList.map((event: any) => {
          if (event.bid_response.id === updatedBid.id) {
            return {
              ...event,
              bid_response: updatedBid,
            }
          } else {
            return event
          }
        })

        state.eventList = eventList
      })

      // fetchAssociatedPlansAction
      .addCase(fetchAssociatedPlansAction.pending, (state, action) => {
        const eventId = action.meta.arg.eventId
        state.eventPlansMap = {
          [eventId]: { isFetchingAssociatedPlans: true, planList: [] },
        }
      })
      .addCase(fetchAssociatedPlansAction.fulfilled, (state, action) => {
        const eventId = action.meta.arg.eventId
        state.eventPlansMap = {
          ...state.eventPlansMap,
          [eventId]: {
            isFetchingAssociatedPlans: false,
            planList: action.payload.data,
          },
        }
      })
      .addCase(fetchAssociatedPlansAction.rejected, (state, action) => {
        const eventId = action.meta.arg.eventId
        state.eventPlansMap = {
          ...state.eventPlansMap,
          [eventId]: { isFetchingAssociatedPlans: false, planList: [] },
        }
      })

      // fetchVendorCountAction
      .addCase(fetchVendorCountAction.pending, (state, action) => {
        const eventId = action.meta.arg.eventId
        state.eventVendorCountMap = {
          [eventId]: { isFetchingVendorCount: true, vendorCount: '' },
        }
      })
      .addCase(fetchVendorCountAction.fulfilled, (state, action) => {
        const eventId = action.meta.arg.eventId
        state.eventVendorCountMap = {
          ...state.eventVendorCountMap,
          [eventId]: {
            isFetchingVendorCount: false,
            vendorCount: action.payload.data.total_count,
          },
        }
      })
      .addCase(fetchVendorCountAction.rejected, (state, action) => {
        const eventId = action.meta.arg.eventId
        state.eventVendorCountMap = {
          ...state.eventVendorCountMap,
          [eventId]: { isFetchingVendorCount: true, vendorCount: '' },
        }
      })

      // fetchProductCountAction
      .addCase(fetchProductCountAction.pending, (state, action) => {
        const { bpId, eventId } = action.meta.arg
        state.eventProductCountMap = {
          [bpId ? bpId : eventId]: {
            isFetchingProductCount: true,
            productCount: '',
          },
        }
      })
      .addCase(fetchProductCountAction.fulfilled, (state, action) => {
        const { bpId, eventId } = action.meta.arg
        state.eventProductCountMap = {
          ...state.eventProductCountMap,
          [bpId ? bpId : eventId]: {
            isFetchingProductCount: false,
            productCount: action.payload.data.count,
          },
        }
      })
      .addCase(fetchProductCountAction.rejected, (state, action) => {
        const { bpId, eventId } = action.meta.arg
        state.eventProductCountMap = {
          ...state.eventProductCountMap,
          [bpId ? bpId : eventId]: {
            isFetchingProductCount: true,
            productCount: '',
          },
        }
      })

      // searchEventsInternalAction
      .addCase(searchEventsInternalAction.pending, state => {
        state.isFetchingEvents = true
      })
      .addCase(searchEventsInternalAction.fulfilled, (state, action) => {
        const { events, per_page, total_count } = action.payload.data
        state.isFetchingEvents = false
        state.eventList = events
        state.eventsPageCount = per_page
        state.eventsTotalCount = total_count
      })
      .addCase(searchEventsInternalAction.rejected, state => {
        state.isFetchingEvents = false
        state.eventList = []
        state.eventsPageCount = 0
        state.eventsTotalCount = 0
      })

      // getAuthUsersAction
      .addCase(getAuthUsersAction.pending, state => {
        state.isFetchingAuthUsers = true
      })
      .addCase(getAuthUsersAction.fulfilled, (state, action) => {
        state.isFetchingAuthUsers = false
        state.authUserList = action.payload
      })
      .addCase(getAuthUsersAction.rejected, state => {
        state.isFetchingAuthUsers = false
        state.authUserList = []
      })

      // getEventUserViewsAction
      .addCase(getEventUserViewsAction.pending, state => {
        state.isFetchingEventUserViews = true
      })
      .addCase(getEventUserViewsAction.fulfilled, (state, action) => {
        const { authUsers, view, event } = action.payload

        state.isFetchingEventUserViews = false
        state.authUserListWithView = authUsers
        state.viewList = view
        state.event = event
      })
      .addCase(getEventUserViewsAction.rejected, state => {
        state.isFetchingEventUserViews = false
        state.authUserListWithView = {}
        state.viewList = []
        state.event = {}
      })

      // fetchProductCountActionForBp
      .addCase(fetchProductCountActionForBp.pending, (state, action) => {
        const { bpId } = action.meta.arg
        state.eventProductCountForBpMap = {
          [bpId]: {
            isFetchingProductCountForBp: true,
            productCount: '',
          },
        }
      })
      .addCase(fetchProductCountActionForBp.fulfilled, (state, action) => {
        const { bpId } = action.meta.arg
        state.eventProductCountForBpMap = {
          ...state.eventProductCountForBpMap,
          [bpId]: {
            isFetchingProductCountForBp: false,
            productCount: action.payload.data.count,
          },
        }
      })
      .addCase(fetchProductCountActionForBp.rejected, (state, action) => {
        const { bpId } = action.meta.arg
        state.eventProductCountForBpMap = {
          ...state.eventProductCountForBpMap,
          [bpId]: {
            isFetchingProductCountForBp: true,
            productCount: '',
          },
        }
      })

      // fetchDistBps
      .addCase(fetchDistBps.pending, state => {
        state.isFetchingDistBps = true
      })
      .addCase(fetchDistBps.fulfilled, (state, action) => {
        state.distBpsList = action.payload.data
        state.isFetchingDistBps = false
      })
      .addCase(fetchDistBps.rejected, state => {
        state.distBpsList = []
        state.isFetchingDistBps = false
      })
  },
})

export const getDistBpsList = state => state?.events?.distBpsList
export const getIsFetchingDistBps = state => state?.events?.isFetchingDistBps
export const getEventList = state => state?.events?.eventList
export const getEventsTotalCount = state => state?.events?.eventsTotalCount
export const getIsFetchingEvents = state => state?.events?.isFetchingEvents
export const getArchivedEventsList = state => state?.events?.archivedEventsList
export const getArchivedEventsTotalCount = state =>
  state?.events?.archivedEventsTotalCount
export const getIsFetchingArchivedEvents = state =>
  state?.events?.isFetchingArchivedEvents

export const {
  setSearchCriteriaAction,
  clearSearchCriteriaAction,
  resetEventsAction,
  resetEventUserViewsAction,
} = eventsSlice.actions

export default eventsSlice.reducer
